/**
 * Orders map view — Leaflet rendition of the orders list.
 *
 * Shows one circle marker per order located at the customer's delivery
 * coordinates (`Order.user_snapshot.latitude/longitude`) and a square
 * marker per unique store, all on an OpenStreetMap tile layer. Marker
 * tone is driven by the order's status via the shared `statusMeta` map
 * so it stays in sync with the rest of the BI palette.
 *
 * Clicking a marker opens a popup with the order's customer, total and
 * status, and a deep-link to `/orders/{id}`. We keep it deliberately
 * lightweight — no clustering, no heat overlay — because the underlying
 * `/api/bi/orders/points/` endpoint already caps at a few thousand
 * points per query.
 */
import { useEffect, useMemo } from 'react';
import {
  CircleMarker,
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

import type { OrderPoint, OrderPointsStore } from '@/api/bi/types';
import { fmt } from '@/lib/format';
import { statusLabel, statusMeta } from '@/lib/status';
import { Badge } from '@/components/ui/Badge';

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function storeIcon(label: string | null): L.DivIcon {
  return L.divIcon({
    className: 'orders-map-store',
    html: `<span style="
      display:inline-flex;align-items:center;justify-content:center;
      width:22px;height:22px;border-radius:6px;
      background:#0f172a;color:white;font-size:11px;font-weight:700;
      box-shadow:0 0 0 2px white, 0 1px 3px rgba(0,0,0,.4);
    ">S</span>${
      label
        ? `<span style="
            position:absolute;left:26px;top:0;
            font-size:10px;font-weight:600;color:#0f172a;
            padding:2px 6px;border-radius:4px;background:white;
            box-shadow:0 1px 3px rgba(0,0,0,.18);white-space:nowrap;
          ">${escapeHtml(label)}</span>`
        : ''
    }`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

/**
 * Resolve a hex/rgb-friendly fill for a status circle. The status palette
 * is keyed by Tailwind tokens, but Leaflet needs raw colours, so we map
 * tone → CSS variable lookups (with safe fallbacks).
 */
function statusFill(status: string): string {
  const tone = statusMeta(status).tone;
  switch (tone) {
    case 'success':
      return '#16a34a';
    case 'warning':
      return '#d97706';
    case 'danger':
      return '#dc2626';
    case 'primary':
      return '#2563eb';
    case 'info':
      return '#0ea5e9';
    case 'neutral':
    default:
      return '#475569';
  }
}

function FitBounds({ points }: { points: L.LatLngTuple[] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 14);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [32, 32], maxZoom: 16 });
  }, [map, points]);
  return null;
}

export interface OrdersMapViewProps {
  points: OrderPoint[];
  stores: OrderPointsStore[];
  onOrderClick?: (id: number) => void;
  onCustomerClick?: (id: number) => void;
  /** When set, map popups can open rider delivery history (staff `/staff/api/orders/?rider_id=`). */
  onRiderClick?: (riderId: number, riderName: string | null, storeId: number | null) => void;
  height?: number | string;
  className?: string;
}

export function OrdersMapView({
  points,
  stores,
  onOrderClick,
  onCustomerClick,
  onRiderClick,
  height = 520,
  className,
}: OrdersMapViewProps) {
  const validPoints = useMemo(
    () => points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)),
    [points],
  );

  const validStores = useMemo(
    () =>
      stores.filter(
        (s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude),
      ),
    [stores],
  );

  const allPositions: L.LatLngTuple[] = useMemo(() => {
    const out: L.LatLngTuple[] = [];
    for (const p of validPoints) out.push([p.lat, p.lng]);
    for (const s of validStores) out.push([s.latitude, s.longitude]);
    return out;
  }, [validPoints, validStores]);

  const center: L.LatLngTuple = allPositions[0] ?? [27.7172, 85.324];

  return (
    <div className={className} style={{ height, position: 'relative', isolation: 'isolate', zIndex: 0 }}>
      <MapContainer
        center={center}
        zoom={12}
        scrollWheelZoom
        style={{ height: '100%', width: '100%', borderRadius: 'inherit', position: 'relative', zIndex: 0 }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={allPositions} />

        {validStores.map((s) => (
          <Marker
            key={`store-${s.id}`}
            position={[s.latitude, s.longitude]}
            icon={storeIcon(s.name)}
            zIndexOffset={500}
          >
            <Popup>
              <div className="text-xs">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Store
                </div>
                <div className="mt-0.5 text-sm font-semibold text-slate-900">
                  {s.name ?? `Store #${s.id}`}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}

        {validPoints.map((p) => {
          const fill = statusFill(p.status);
          return (
            <CircleMarker
              key={`order-${p.id}`}
              center={[p.lat, p.lng]}
              radius={6}
              pathOptions={{
                color: fill,
                weight: 1.5,
                fillColor: fill,
                fillOpacity: 0.75,
              }}
            >
              <Popup>
                <OrderMarkerPopup
                  point={p}
                  onOrderClick={onOrderClick}
                  onCustomerClick={onCustomerClick}
                  onRiderClick={onRiderClick}
                />
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}

interface OrderMarkerPopupProps {
  point: OrderPoint;
  onOrderClick?: (id: number) => void;
  onCustomerClick?: (id: number) => void;
  onRiderClick?: (riderId: number, riderName: string | null, storeId: number | null) => void;
}

function OrderMarkerPopup({
  point,
  onOrderClick,
  onCustomerClick: _onCustomerClick,
  onRiderClick,
}: OrderMarkerPopupProps) {
  return (
    <div className="min-w-[210px] text-xs">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => onOrderClick?.(point.id)}
          className="kpi-num text-sm font-semibold text-blue-600 hover:underline"
        >
          #{point.id}
        </button>
        <Badge tone={statusMeta(point.status).tone}>{statusLabel(point.status)}</Badge>
      </div>
      <div className="mt-1.5 truncate font-medium text-slate-900">
        {point.customer_name ?? 'Customer'}
      </div>
      {point.customer_phone ? (
        <a
          href={`tel:${point.customer_phone}`}
          className="kpi-num text-[11px] text-slate-500 hover:text-slate-700"
        >
          {point.customer_phone}
        </a>
      ) : null}
      {point.customer_address ? (
        <div className="mt-0.5 line-clamp-2 text-[11px] text-slate-500">
          {point.customer_address}
        </div>
      ) : null}
      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
        <span>{point.store_name ?? '—'}</span>
        <span className="kpi-num font-semibold text-slate-900">
          {fmt.npr(point.final_total)}
        </span>
      </div>
      {point.rider_name || point.rider_id != null ? (
        <div className="mt-1 text-[11px] text-slate-500">
          Rider ·{' '}
          {point.rider_id != null && onRiderClick ? (
            <button
              type="button"
              onClick={() => onRiderClick(point.rider_id!, point.rider_name, point.store_id)}
              className="font-medium text-slate-800 underline-offset-2 hover:text-blue-700 hover:underline"
            >
              {point.rider_name ?? `#${point.rider_id}`}
            </button>
          ) : (
            <span className="text-slate-700">{point.rider_name ?? '—'}</span>
          )}
        </div>
      ) : null}
      {point.created_timestamp ? (
        <div className="mt-1 text-[11px] text-slate-400">
          {fmt.datetime(point.created_timestamp)}
        </div>
      ) : null}
    </div>
  );
}
